<script setup>
import { ref, onMounted, watchEffect } from "vue";
import * as d3 from "d3";
import { useResizeObserver } from "@/services";
import {
  CANDLE_GREEN_COLOR,
  CANDLE_RED_COLOR,
  CANDLE_GRAY_COLOR,
  AREA_COLOR_MAP,
  COLOR_BG2,
} from "@/constants";

const props = defineProps({
  candles: Array,
  areas: Array,
  exchange: String,
  symbol: String,
});

const colors = [CANDLE_GREEN_COLOR, CANDLE_GRAY_COLOR, CANDLE_RED_COLOR];

const svgRef = ref(null);

const { resizeRef, resizeState } = useResizeObserver();

onMounted(() => {
  watchEffect(() => {
    render();
  });
});

const render = () => {
  let { width, height } = resizeState.dimensions;

  if (width < 600) {
    width = 600;
  }

  const X = d3.map(props.candles, (i) => new Date(i[0] * 1000));
  const Yo = d3.map(props.candles, (i) => parseFloat(i[1]));
  const Yh = d3.map(props.candles, (i) => parseFloat(i[2]));
  const Yl = d3.map(props.candles, (i) => parseFloat(i[3]));
  const Yc = d3.map(props.candles, (i) => parseFloat(i[4]));
  const Yv = d3.map(props.candles, (i) => parseFloat(i[5]));
  const Yf = props.areas.flatMap((f) => f.points.map((d) => d[1]));
  const I = d3.range(X.length);

  const marginLeft = 60;
  const marginRight = 20;
  const marginTop = 20;
  const marginBottom = 20;
  const volumeHeight = height / 6;
  const xPadding = 0.2;
  const xRange = [marginLeft, width - marginRight];
  const yRange = [height - marginBottom, marginTop];
  const vRange = [height - marginBottom, height - marginBottom - volumeHeight];

  const weeks = (start, stop, stride) =>
    d3.utcMonday.every(stride).range(start, +stop + 1);
  const weekdays = (start, stop) =>
    d3
      .utcDays(start, +stop + 1)
      .filter((d) => true); /* d.getUTCDay() !== 0 && d.getUTCDay() !== 6 */

  /* const xDomain = weekdays(d3.min(X), d3.max(X)); */
  const xDomain = X;
  const yDomain = [d3.min(Yl.concat(Yf)), d3.max(Yh.concat(Yf))];
  const vDomain = [0, d3.max(Yv)];
  const xTicks = weeks(d3.min(xDomain), d3.max(xDomain), 2);

  const xScale = d3.scaleBand(xDomain, xRange).padding(xPadding);
  const yScale = d3.scaleLinear(yDomain, yRange);
  const vScale = d3.scaleLinear(vDomain, vRange);
  const xAxis = d3
    .axisBottom(xScale)
    .tickFormat(d3.utcFormat("%b %-d"))
    .tickValues(xTicks);
  const yAxis = d3.axisLeft(yScale).ticks(height / 40, "~f");

  const container = d3.select(svgRef.value);

  container.selectAll("g").remove();

  container
    .append("g")
    .attr("transform", `translate(0,${height - marginBottom})`)
    .call(xAxis)
    .call((g) => g.select(".domain").remove())
    .call((g) =>
      g
        .selectAll(".tick line")
        .clone()
        .attr("stroke-opacity", 0.2)
        .attr("y1", marginBottom - height + marginTop)
    );

  container
    .append("g")
    .attr("transform", `translate(${marginLeft},0)`)
    .call(yAxis)
    .call((g) => g.select(".domain").remove())
    .call((g) =>
      g
        .selectAll(".tick line")
        .clone()
        .attr("stroke-opacity", 0.2)
        .attr("x2", width - marginLeft - marginRight)
    )
    .call((g) =>
      g
        .append("text")
        .attr("x", -marginLeft)
        .attr("y", 10)
        .attr("fill", "currentColor")
        .attr("text-anchor", "start")
        .text("Price")
    );

  container
    .append("g")
    .attr("transform", `translate(${marginLeft},0)`)
    .call((g) =>
      g
        .append("text")
        .attr("x", 10)
        .attr("y", 40)
        .attr("fill", "currentColor")
        .attr("text-anchor", "start")
        .text(props.symbol)
    );

  container
    .append("g")
    .attr("transform", `translate(${marginLeft},0)`)
    .call((g) =>
      g
        .append("text")
        .attr("x", 10)
        .attr("y", 60)
        .attr("fill", "currentColor")
        .attr("text-anchor", "start")
        .attr("font-size", "14")
        .text(props.exchange)
    );

  const g = container
    .append("g")
    .attr("stroke", "currentColor")
    .selectAll("g")
    .data(I)
    .join("g")
    .attr("transform", (i) => `translate(${xScale(X[i])},0)`);

  g.append("line")
    .attr("y1", (i) => yScale(Yl[i]))
    .attr("y2", (i) => yScale(Yh[i]))
    .attr("stroke", (i) => colors[1 + Math.sign(Yo[i] - Yc[i])]);

  g.append("line")
    .attr("y1", (i) => yScale(Yo[i]))
    .attr("y2", (i) => yScale(Yc[i]))
    .attr("stroke-width", xScale.bandwidth())
    .attr("stroke", (i) => colors[1 + Math.sign(Yo[i] - Yc[i])]);

  container
    .append("g")
    .attr("stroke", "currentColor")
    .selectAll("g")
    .data(I)
    .join("g")
    .attr("transform", (i) => `translate(${xScale(X[i])},0)`)
    .append("line")
    .attr("y1", () => vScale(0))
    .attr("y2", (i) => vScale(Yv[i]))
    .attr("stroke-width", xScale.bandwidth())
    .attr("stroke-opacity", 0.25)
    .attr("stroke", (i) => colors[1 + Math.sign(Yo[i] - Yc[i])]);

  container
    .append("g")
    .selectAll("polygon")
    .data(props.areas.filter((d) => !d.hide))
    .enter()
    .append("polygon")
    .attr("points", function (d) {
      return d.points
        .map(function (d) {
          return [xScale(new Date(d[0] * 1000)), yScale(d[1])].join(",");
        })
        .join(" ");
    })
    .attr("stroke", (d) => AREA_COLOR_MAP[d.type])
    .attr("fill", (d) => AREA_COLOR_MAP[d.type])
    .attr("fill-opacity", 0.5)
    .attr("stroke-width", 0);

  container.attr("width", width).attr("height", height);

  return { svgRef, resizeRef };
};
</script>

<template>
  <div
    ref="resizeRef"
    class="overflow-scroll relative p-2"
    :style="{ 'background-color': COLOR_BG2 }"
  >
    <svg ref="svgRef" class="absolute"></svg>
  </div>
</template>

<style scoped></style>
